import type { Rect, Vec2 } from "../types";
import { insideCorridors, insideRect } from "./geometry";

export interface OpenDirections {
  readonly left: boolean;
  readonly right: boolean;
  readonly up: boolean;
  readonly down: boolean;
}

/**
 * Which of the 4 cardinal directions are walkable from `cursor` right now: a probe
 * point `probePx` away along that axis still fits a `radius`-disc inside the union of
 * `corridors` (reusing `insideCorridors` — same disc-fit test `stepGame`'s sweep uses,
 * not reimplemented here). Screen axes: left = -x, right = +x, up = -y, down = +y.
 */
export function openDirections(
  cursor: Vec2,
  corridors: Rect[],
  radius: number,
  probePx = 24,
): OpenDirections {
  return {
    left: insideCorridors({ x: cursor.x - probePx, y: cursor.y }, corridors, radius),
    right: insideCorridors({ x: cursor.x + probePx, y: cursor.y }, corridors, radius),
    up: insideCorridors({ x: cursor.x, y: cursor.y - probePx }, corridors, radius),
    down: insideCorridors({ x: cursor.x, y: cursor.y + probePx }, corridors, radius),
  };
}

/**
 * The dominant-axis cardinal direction from `cursor` toward `goalRect`'s centre —
 * "none" when `cursor` is already inside the goal. Ties on `|dx| === |dy|` resolve to
 * the x-axis (arbitrary but deterministic).
 */
export function directionToGoal(
  cursor: Vec2,
  goalRect: Rect,
): "left" | "right" | "up" | "down" | "none" {
  if (insideRect(cursor, goalRect)) return "none";

  const centerX = goalRect.x + goalRect.w / 2;
  const centerY = goalRect.y + goalRect.h / 2;
  const dx = centerX - cursor.x;
  const dy = centerY - cursor.y;

  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0 ? "right" : "left";
  }
  return dy >= 0 ? "down" : "up";
}

/**
 * Signed perpendicular offset (px) of `cursor` from the centreline of whichever
 * `corridors` rect contains it first (array order — the caller's rects can overlap at
 * an L-join; this doesn't try to disambiguate), 0 when `cursor` is outside every rect.
 *
 * A corridor's centreline runs along its long axis, so the perpendicular axis is the
 * *short* one: for a horizontal rect (`w >= h`, long axis x) that's y; for a vertical
 * rect (long axis y) that's x. Sign matches `src/types.ts`'s documented gaze/screen
 * convention ("x: right positive, y: down positive") applied to whichever axis is
 * perpendicular here — so a horizontal corridor's offset is positive when the cursor
 * sits *below* centre (screen-down), and a vertical corridor's offset is positive when
 * the cursor sits *right* of centre (screen-right).
 */
export function signedCentrelineOffset(cursor: Vec2, corridors: Rect[]): number {
  for (const r of corridors) {
    if (!insideRect(cursor, r)) continue;
    if (r.w >= r.h) {
      return cursor.y - (r.y + r.h / 2);
    }
    return cursor.x - (r.x + r.w / 2);
  }
  return 0;
}
